/**
 * Single source of truth for the phone country codes shown across the app
 * (login, doctor registration, etc.).
 *
 * `iso` is the lowercase ISO 3166-1 alpha-2 code used to build the flag image
 * URL from flagcdn.com, e.g. https://flagcdn.com/24x18/iq.png
 *
 * Iraq is first because it is the primary market and therefore the default.
 */
export const COUNTRIES = [
  { code: "+964", iso: "iq", name: "Iraq" },
  { code: "+91", iso: "in", name: "India" },
  { code: "+966", iso: "sa", name: "Saudi Arabia" },
  { code: "+971", iso: "ae", name: "UAE" },
  { code: "+965", iso: "kw", name: "Kuwait" },
  { code: "+974", iso: "qa", name: "Qatar" },
  { code: "+973", iso: "bh", name: "Bahrain" },
  { code: "+968", iso: "om", name: "Oman" },
  { code: "+962", iso: "jo", name: "Jordan" },
  { code: "+961", iso: "lb", name: "Lebanon" },
  { code: "+20", iso: "eg", name: "Egypt" },
  { code: "+90", iso: "tr", name: "Turkey" },
  { code: "+98", iso: "ir", name: "Iran" },
  { code: "+92", iso: "pk", name: "Pakistan" },
  { code: "+880", iso: "bd", name: "Bangladesh" },
  { code: "+44", iso: "gb", name: "United Kingdom" },
  { code: "+1", iso: "us", name: "United States" },
  { code: "+49", iso: "de", name: "Germany" },
  { code: "+33", iso: "fr", name: "France" },
  { code: "+61", iso: "au", name: "Australia" },
  { code: "+86", iso: "cn", name: "China" },
  { code: "+81", iso: "jp", name: "Japan" },
  { code: "+82", iso: "kr", name: "South Korea" },
  { code: "+60", iso: "my", name: "Malaysia" },
  { code: "+63", iso: "ph", name: "Philippines" },
  { code: "+234", iso: "ng", name: "Nigeria" },
  { code: "+27", iso: "za", name: "South Africa" },
  { code: "+254", iso: "ke", name: "Kenya" },
  { code: "+55", iso: "br", name: "Brazil" },
  { code: "+52", iso: "mx", name: "Mexico" },
];

/** Dial code used when nothing else can be resolved (primary market). */
export const DEFAULT_COUNTRY_CODE = COUNTRIES[0].code;

/** Flag image URL (24x18) for an ISO alpha-2 country code. */
export const flagUrl = (iso) => `https://flagcdn.com/24x18/${iso}.png`;

/** Resolve a dial code (e.g. "+964") to its country, falling back to the first. */
export const findCountryByCode = (code) =>
  COUNTRIES.find((country) => country.code === code) || COUNTRIES[0];

/** True when the given dial code is one we actually offer in the picker. */
export const isKnownCountryCode = (code) =>
  COUNTRIES.some((country) => country.code === code);

/**
 * Split a stored phone number into its dial code and national part.
 *
 * Numbers are persisted in full international form (e.g. "+917847838577"), so the
 * dial code is recovered with a longest-prefix match to avoid ambiguity between
 * overlapping codes (e.g. "+1" vs "+964").
 *
 * @param {string} fullNumber  e.g. "+917847838577" or "917847838577"
 * @param {string} [fallbackCode]  dial code to assume when no prefix matches
 * @returns {{ code: string, national: string }}
 */
export const splitDialCode = (fullNumber, fallbackCode = DEFAULT_COUNTRY_CODE) => {
  const digits = String(fullNumber || "").replace(/[^\d]/g, "");

  if (!digits) return { code: fallbackCode, national: "" };

  const match = [...COUNTRIES]
    .sort((a, b) => b.code.length - a.code.length)
    .find((country) => digits.startsWith(country.code.replace("+", "")));

  if (!match) return { code: fallbackCode, national: digits };

  return {
    code: match.code,
    national: digits.slice(match.code.length - 1),
  };
};
